import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchQuotesTable } from '../api/quotesApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

export default function useQuotes(filter) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['quotes', filter],
        queryFn: () => fetchQuotesTable(filter),
        placeholderData: (prev) => prev,
    });

    // Capture the customer scope for this hook instance. For the global
    // QuotesPage there is no customerId so every UpdateQuoteTable fires.
    // For ViewCustomerPage the customerId is stable (from the URL param) so
    // closing over it here is safe — a different customer would unmount and
    // remount the page, re-running this effect with the new value.
    const scopedCustomerId = filter.customerId ?? null;

    useEffect(() => {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                // Clerk issues short-lived tokens; SignalR calls this factory
                // on connect and on reconnect, so we always hand it a fresh one.
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        connection.on('UpdateQuoteTable', (eventCustomerId) => {
            // Scoped tables only refresh when the broadcast is for their
            // customer (or a global null event). Global tables always refresh.
            if (!scopedCustomerId || eventCustomerId == null || eventCustomerId === scopedCustomerId) {
                queryClient.invalidateQueries({ queryKey: ['quotes'] });
            }
        });

        connection.start().catch(err => console.error('SignalR connection error:', err));

        return () => {
            connection.stop();
        };
    }, [queryClient, scopedCustomerId]);

    return query;
}
